// TrainingPanel - UI for model training feedback
// Dual mode: Item-by-item review OR Bulk conditions

import { trainingApi, type TrainingItem, type TrainingStats } from '../services/training-api';
import { audienceApi } from '../services/audience-api';

export class TrainingPanel {
  private containerEl: HTMLElement;
  private mode: 'item' | 'bulk' = 'item';
  private stats: TrainingStats | null = null;
  private currentItem: TrainingItem | null = null;
  private currentIndex: number = 0;
  private items: TrainingItem[] = [];
  private selectedItems: Set<string> = new Set();
  private audiences: any[] = [];

  constructor(containerEl: HTMLElement) {
    this.containerEl = containerEl;
  }

  async load(): Promise<void> {
    await this.loadStats();
    await this.loadAudiences();
    await this.loadItems();
    this.render();
  }

  private async loadStats(): Promise<void> {
    try {
      this.stats = await trainingApi.getStats();
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }

  private async loadAudiences(): Promise<void> {
    try {
      const products = await audienceApi.listProducts();
      for (const product of products) {
        const productAudiences = await audienceApi.listAudiences(product.id);
        this.audiences.push(...productAudiences);
      }
    } catch (error) {
      console.error('Failed to load audiences:', error);
    }
  }

  private async loadItems(): Promise<void> {
    try {
      this.items = await trainingApi.getTrainingItems({
        feedbackType: 'audience_classification',
        validated: false,
        limit: 50
      });

      if (this.items.length > 0 && !this.currentItem) {
        this.currentItem = this.items[0];
        this.currentIndex = 0;
      }
    } catch (error) {
      console.error('Failed to load items:', error);
    }
  }

  private render(): void {
    this.containerEl.innerHTML = `
      <div class="training-container">
        <!-- Mode Toggle -->
        <div class="training-header">
          <h3>Training Feedback</h3>
          <div class="training-mode-toggle">
            <button class="mode-btn ${this.mode === 'item' ? 'active' : ''}" data-mode="item">
              <span class="material-symbols-outlined">assignment</span>
              Item by Item
            </button>
            <button class="mode-btn ${this.mode === 'bulk' ? 'active' : ''}" data-mode="bulk">
              <span class="material-symbols-outlined">checklist</span>
              Bulk Conditions
            </button>
          </div>
        </div>

        <!-- Stats Card -->
        ${this.renderStats()}

        <!-- Content (mode-specific) -->
        <div class="training-content">
          ${this.mode === 'item' ? this.renderItemMode() : this.renderBulkMode()}
        </div>

        <!-- Export Section -->
        ${this.renderExport()}
      </div>
    `;

    this.attachEventListeners();
  }

  private renderStats(): string {
    if (!this.stats) return '';

    const audienceProgress = this.stats.audienceClassification.total > 0
      ? (this.stats.audienceClassification.validated / this.stats.audienceClassification.total) * 100
      : 0;

    return `
      <div class="training-stats-card">
        <h4>Training Progress</h4>
        <div class="stats-grid">
          <div class="stat-item">
            <div class="stat-label">Audience Classification</div>
            <div class="stat-value">${this.stats.audienceClassification.validated} / ${this.stats.audienceClassification.total}</div>
            <div class="stat-progress">
              <div class="stat-progress-bar" style="width: ${audienceProgress}%"></div>
            </div>
            <div class="stat-note">${this.stats.readyForTraining.audienceClassification ? '✓ Ready for training!' : `Need ${100 - this.stats.audienceClassification.validated}+ for training`}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">Prefill Examples</div>
            <div class="stat-value">${this.stats.prefill.total}</div>
            <div class="stat-note">${this.stats.readyForTraining.prefill ? '✓ Ready' : 'Need 100+ for training'}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">Playbook Examples</div>
            <div class="stat-value">${this.stats.playbook.total}</div>
            <div class="stat-note">${this.stats.readyForTraining.playbook ? '✓ Ready' : 'Need 50+ for training'}</div>
          </div>
        </div>
      </div>
    `;
  }

  private renderItemMode(): string {
    if (!this.currentItem) {
      return `
        <div class="empty-state">
          <span class="material-symbols-outlined">check_circle</span>
          <p>All items validated! No pending items.</p>
        </div>
      `;
    }

    const audienceNames = this.currentItem.classification.audienceMatches?.map((match: any) => {
      const aud = this.audiences.find(a => a.id === match.audienceId);
      return aud ? aud.name : 'Unknown';
    }) || [];

    return `
      <div class="item-review-container">
        <!-- Progress -->
        <div class="review-progress">
          <span>Item ${this.currentIndex + 1} of ${this.items.length}</span>
          <div class="progress-bar">
            <div class="progress-fill" style="width: ${((this.currentIndex + 1) / this.items.length) * 100}%"></div>
          </div>
        </div>

        <!-- Resource Info -->
        <div class="resource-card">
          <h4>${this.currentItem.title || 'Untitled'}</h4>
          ${this.currentItem.summary ? `<p class="resource-summary">${this.currentItem.summary}</p>` : ''}
          <div class="resource-meta">
            <span class="badge">${this.currentItem.category || 'Uncategorized'}</span>
            <span class="resource-date">${new Date(this.currentItem.classifiedAt).toLocaleDateString()}</span>
          </div>
        </div>

        <!-- Classification Review -->
        <div class="classification-review">
          <h5>AI Classification</h5>

          <div class="classification-field">
            <label>Purpose</label>
            <div class="classification-value">
              <span class="badge badge-purpose">${this.currentItem.classification.purpose || 'Not detected'}</span>
            </div>
          </div>

          <div class="classification-field">
            <label>Matched Audiences</label>
            <div class="classification-value">
              ${this.currentItem.classification.audienceMatches?.length ? `
                ${this.currentItem.classification.audienceMatches.map((match: any, idx: number) => `
                  <div class="audience-match">
                    <span class="audience-name">${audienceNames[idx] || 'Unknown'}</span>
                    <span class="match-score">${(match.score * 100).toFixed(0)}%</span>
                  </div>
                  ${match.reasoning ? `<p class="match-reasoning">${match.reasoning}</p>` : ''}
                `).join('')}
              ` : '<span class="text-muted">No matches</span>'}
            </div>
          </div>

          <div class="classification-field">
            <label>Problems Detected</label>
            <div class="classification-value">
              ${this.currentItem.classification.problems?.length ? `
                <ul class="problems-list">
                  ${this.currentItem.classification.problems.map((p: string) => `<li>${p}</li>`).join('')}
                </ul>
              ` : '<span class="text-muted">None detected</span>'}
            </div>
          </div>

          <div class="classification-field">
            <label>Suggested Angles</label>
            <div class="classification-value">
              ${this.currentItem.classification.angles?.length ? `
                <ul class="angles-list">
                  ${this.currentItem.classification.angles.map((a: string) => `<li>${a}</li>`).join('')}
                </ul>
              ` : '<span class="text-muted">None suggested</span>'}
            </div>
          </div>

          <div class="classification-field">
            <label>Awareness Level & Funnel Stage</label>
            <div class="classification-value">
              <span class="badge">${this.currentItem.classification.awarenessLevel || 'Not set'}</span>
              <span class="badge">${this.currentItem.classification.funnelStage?.toUpperCase() || 'Not set'}</span>
            </div>
          </div>
        </div>

        <!-- Feedback Actions -->
        <div class="feedback-actions">
          <button class="btn-success btn-lg" id="btn-approve">
            <span class="material-symbols-outlined">thumb_up</span>
            Correct - Approve
          </button>
          <button class="btn-danger btn-lg" id="btn-reject">
            <span class="material-symbols-outlined">thumb_down</span>
            Incorrect - Reject
          </button>
          <button class="btn-secondary" id="btn-skip">
            Skip for Now
          </button>
          <button class="btn-secondary" id="btn-next-similar">
            <span class="material-symbols-outlined">double_arrow</span>
            Next Similar
          </button>
        </div>
      </div>
    `;
  }

  private renderBulkMode(): string {
    return `
      <div class="bulk-mode-container">
        <!-- Filters -->
        <div class="bulk-filters">
          <h4>Select Items by Conditions</h4>
          <div class="filter-grid">
            <div class="filter-field">
              <label>Audience</label>
              <select id="bulk-audience-filter" class="select-input">
                <option value="">All Audiences</option>
                ${this.audiences.map(a => `
                  <option value="${a.id}">${a.name}</option>
                `).join('')}
              </select>
            </div>
            <div class="filter-field">
              <label>Purpose</label>
              <select id="bulk-purpose-filter" class="select-input">
                <option value="">All</option>
                <option value="content_idea">Content Idea</option>
                <option value="learning">Learning</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div class="filter-field">
              <label>Funnel Stage</label>
              <select id="bulk-funnel-filter" class="select-input">
                <option value="">All</option>
                <option value="tofu">TOFU</option>
                <option value="mofu">MOFU</option>
                <option value="bofu">BOFU</option>
              </select>
            </div>
          </div>
          <button class="btn-primary" id="btn-apply-filters">
            <span class="material-symbols-outlined">filter_list</span>
            Apply Filters
          </button>
        </div>

        <!-- Results -->
        <div class="bulk-results">
          <div class="bulk-results-header">
            <span>${this.selectedItems.size} items selected</span>
            <div>
              <button class="btn-secondary btn-sm" id="btn-select-all">Select All</button>
              <button class="btn-secondary btn-sm" id="btn-deselect-all">Deselect All</button>
            </div>
          </div>

          <div class="bulk-items-list">
            ${this.items.length === 0 ? `
              <div class="empty-state">
                <span class="material-symbols-outlined">filter_alt</span>
                <p>Apply filters to see matching items</p>
              </div>
            ` : `
              ${this.items.map(item => `
                <div class="bulk-item ${this.selectedItems.has(item.resourceId) ? 'selected' : ''}">
                  <input type="checkbox"
                    class="bulk-item-checkbox"
                    data-resource-id="${item.resourceId}"
                    ${this.selectedItems.has(item.resourceId) ? 'checked' : ''}>
                  <div class="bulk-item-info">
                    <div class="bulk-item-title">${item.title || 'Untitled'}</div>
                    <div class="bulk-item-meta">
                      <span class="badge">${item.classification.purpose || 'N/A'}</span>
                      <span class="badge">${item.classification.funnelStage?.toUpperCase() || 'N/A'}</span>
                      <span class="text-muted">${new Date(item.classifiedAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              `).join('')}
            `}
          </div>
        </div>

        <!-- Bulk Actions -->
        <div class="bulk-actions">
          <button class="btn-success" id="btn-bulk-approve" ${this.selectedItems.size === 0 ? 'disabled' : ''}>
            <span class="material-symbols-outlined">check_circle</span>
            Approve Selected (${this.selectedItems.size})
          </button>
          <button class="btn-danger" id="btn-bulk-reject" ${this.selectedItems.size === 0 ? 'disabled' : ''}>
            <span class="material-symbols-outlined">cancel</span>
            Reject Selected (${this.selectedItems.size})
          </button>
        </div>
      </div>
    `;
  }

  private renderExport(): string {
    if (!this.stats) return '';

    return `
      <div class="training-export-card">
        <h4>Export for Fine-Tuning</h4>
        <p class="text-muted">Export validated examples in JSONL format for Unsloth fine-tuning</p>

        <div class="export-grid">
          <div class="export-item">
            <div class="export-label">Audience Classification</div>
            <div class="export-count">${this.stats.audienceClassification.validated} examples</div>
            <button class="btn-secondary btn-sm" id="btn-export-audience"
              ${!this.stats.readyForTraining.audienceClassification ? 'disabled' : ''}>
              Export JSONL
            </button>
          </div>
          <div class="export-item">
            <div class="export-label">Prefill</div>
            <div class="export-count">${this.stats.prefill.total} examples</div>
            <button class="btn-secondary btn-sm" id="btn-export-prefill"
              ${!this.stats.readyForTraining.prefill ? 'disabled' : ''}>
              Export JSONL
            </button>
          </div>
          <div class="export-item">
            <div class="export-label">Playbook</div>
            <div class="export-count">${this.stats.playbook.total} examples</div>
            <button class="btn-secondary btn-sm" id="btn-export-playbook"
              ${!this.stats.readyForTraining.playbook ? 'disabled' : ''}>
              Export JSONL
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private attachEventListeners(): void {
    // Mode toggle
    this.containerEl.querySelectorAll('[data-mode]').forEach(btn => {
      btn.addEventListener('click', () => {
        const mode = (btn as HTMLElement).dataset.mode as 'item' | 'bulk';
        this.mode = mode;
        this.render();
      });
    });

    // Item mode actions
    const approveBtn = this.containerEl.querySelector('#btn-approve');
    if (approveBtn) {
      approveBtn.addEventListener('click', () => this.handleApprove());
    }

    const rejectBtn = this.containerEl.querySelector('#btn-reject');
    if (rejectBtn) {
      rejectBtn.addEventListener('click', () => this.handleReject());
    }

    const skipBtn = this.containerEl.querySelector('#btn-skip');
    if (skipBtn) {
      skipBtn.addEventListener('click', () => this.handleSkip());
    }

    const nextSimilarBtn = this.containerEl.querySelector('#btn-next-similar');
    if (nextSimilarBtn) {
      nextSimilarBtn.addEventListener('click', () => this.handleNextSimilar());
    }

    // Bulk mode actions
    const applyFiltersBtn = this.containerEl.querySelector('#btn-apply-filters');
    if (applyFiltersBtn) {
      applyFiltersBtn.addEventListener('click', () => this.handleApplyFilters());
    }

    const selectAllBtn = this.containerEl.querySelector('#btn-select-all');
    if (selectAllBtn) {
      selectAllBtn.addEventListener('click', () => {
        this.items.forEach(item => this.selectedItems.add(item.resourceId));
        this.render();
      });
    }

    const deselectAllBtn = this.containerEl.querySelector('#btn-deselect-all');
    if (deselectAllBtn) {
      deselectAllBtn.addEventListener('click', () => {
        this.selectedItems.clear();
        this.render();
      });
    }

    // Checkboxes
    this.containerEl.querySelectorAll('.bulk-item-checkbox').forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        const resourceId = (e.target as HTMLInputElement).dataset.resourceId!;
        if ((e.target as HTMLInputElement).checked) {
          this.selectedItems.add(resourceId);
        } else {
          this.selectedItems.delete(resourceId);
        }
        this.render();
      });
    });

    const bulkApproveBtn = this.containerEl.querySelector('#btn-bulk-approve');
    if (bulkApproveBtn) {
      bulkApproveBtn.addEventListener('click', () => this.handleBulkApprove());
    }

    const bulkRejectBtn = this.containerEl.querySelector('#btn-bulk-reject');
    if (bulkRejectBtn) {
      bulkRejectBtn.addEventListener('click', () => this.handleBulkReject());
    }

    // Export buttons
    const exportAudienceBtn = this.containerEl.querySelector('#btn-export-audience');
    if (exportAudienceBtn) {
      exportAudienceBtn.addEventListener('click', () =>
        this.handleExport('audience_classification'));
    }

    const exportPrefillBtn = this.containerEl.querySelector('#btn-export-prefill');
    if (exportPrefillBtn) {
      exportPrefillBtn.addEventListener('click', () =>
        this.handleExport('prefill'));
    }

    const exportPlaybookBtn = this.containerEl.querySelector('#btn-export-playbook');
    if (exportPlaybookBtn) {
      exportPlaybookBtn.addEventListener('click', () =>
        this.handleExport('playbook'));
    }
  }

  private async handleApprove(): Promise<void> {
    if (!this.currentItem) return;

    try {
      await trainingApi.saveFeedback({
        resourceId: this.currentItem.resourceId,
        feedbackType: 'audience_classification',
        originalData: this.currentItem.classification,
        isCorrect: true
      });

      await this.moveToNext();
      await this.loadStats();
    } catch (error) {
      alert(`Error: ${(error as Error).message}`);
    }
  }

  private async handleReject(): Promise<void> {
    if (!this.currentItem) return;

    try {
      const notes = prompt('Optional: Why is this incorrect?');

      await trainingApi.saveFeedback({
        resourceId: this.currentItem.resourceId,
        feedbackType: 'audience_classification',
        originalData: this.currentItem.classification,
        isCorrect: false,
        userNotes: notes || undefined
      });

      await this.moveToNext();
      await this.loadStats();
    } catch (error) {
      alert(`Error: ${(error as Error).message}`);
    }
  }

  private async handleSkip(): Promise<void> {
    await this.moveToNext();
  }

  private async handleNextSimilar(): Promise<void> {
    if (!this.currentItem) return;

    try {
      const next = await trainingApi.getNextSimilar(this.currentItem.resourceId);
      if (next) {
        this.currentItem = next;
        this.render();
      } else {
        alert('No more similar items found');
      }
    } catch (error) {
      alert(`Error: ${(error as Error).message}`);
    }
  }

  private async moveToNext(): Promise<void> {
    this.currentIndex++;
    if (this.currentIndex < this.items.length) {
      this.currentItem = this.items[this.currentIndex];
      this.render();
    } else {
      // Reload items
      await this.loadItems();
      this.render();
    }
  }

  private async handleApplyFilters(): Promise<void> {
    const audienceId = (this.containerEl.querySelector('#bulk-audience-filter') as HTMLSelectElement)?.value;
    const purpose = (this.containerEl.querySelector('#bulk-purpose-filter') as HTMLSelectElement)?.value;
    const funnelStage = (this.containerEl.querySelector('#bulk-funnel-filter') as HTMLSelectElement)?.value;

    try {
      // For simplicity, filter client-side
      // In production, you'd pass these to the API
      this.items = await trainingApi.getTrainingItems({
        feedbackType: 'audience_classification',
        validated: false,
        audienceId: audienceId || undefined,
        limit: 100
      });

      // Client-side filters
      if (purpose) {
        this.items = this.items.filter(item => item.classification.purpose === purpose);
      }
      if (funnelStage) {
        this.items = this.items.filter(item => item.classification.funnelStage === funnelStage);
      }

      this.render();
    } catch (error) {
      alert(`Error: ${(error as Error).message}`);
    }
  }

  private async handleBulkApprove(): Promise<void> {
    if (this.selectedItems.size === 0) return;

    if (!confirm(`Approve ${this.selectedItems.size} items as correct?`)) return;

    try {
      const count = await trainingApi.bulkValidate(Array.from(this.selectedItems), true);
      alert(`${count} items validated successfully`);

      this.selectedItems.clear();
      await this.loadItems();
      await this.loadStats();
      this.render();
    } catch (error) {
      alert(`Error: ${(error as Error).message}`);
    }
  }

  private async handleBulkReject(): Promise<void> {
    if (this.selectedItems.size === 0) return;

    if (!confirm(`Mark ${this.selectedItems.size} items as incorrect?`)) return;

    try {
      const count = await trainingApi.bulkValidate(Array.from(this.selectedItems), false);
      alert(`${count} items marked as incorrect`);

      this.selectedItems.clear();
      await this.loadItems();
      await this.loadStats();
      this.render();
    } catch (error) {
      alert(`Error: ${(error as Error).message}`);
    }
  }

  private async handleExport(modelType: 'audience_classification' | 'prefill' | 'playbook'): Promise<void> {
    try {
      await trainingApi.exportTrainingData(modelType, 'jsonl');
      alert('Training data exported successfully!');
    } catch (error) {
      alert(`Error: ${(error as Error).message}`);
    }
  }
}